import { useEffect, useState } from 'react';
import { useRoutes, useLocation } from 'react-router-dom';
import { Sidebar } from '@/components/ui/Sidebar';
import { Topbar } from '@/components/ui/Topbar';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { CommandPalette } from '@/components/ui/CommandPalette';
import { AskAiDrawer } from '@/components/ui/AskAiDrawer';
import { Footer } from '@/components/ui/Footer';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToastProvider } from '@/components/ui/Toast';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoginPage } from '@/modules/auth/LoginPage';
import { routes } from '@/routes/router';
import { dataService } from '@/services/dataService';

export function App() {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [density, setDensity] = useState<'comfortable' | 'compact'>('comfortable');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [askAiOpen, setAskAiOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const element = useRoutes(routes);
  const location = useLocation();

  // The off-canvas sidebar drawer (mobile only — see global.css's .sidebar
  // media query) must never stay open across a navigation, otherwise the
  // next page loads hidden behind it.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Theme/density/reduced-motion are persisted through dataService
  // (Settings → Appearance), loaded once on mount so a refresh doesn't
  // silently revert them.
  useEffect(() => {
    dataService.getSettings().then((s) => {
      setTheme(s.theme);
      setDensity(s.density);
      document.documentElement.setAttribute('data-reduced-motion', String(s.reducedMotion));
    });
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  async function toggleTheme() {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    await dataService.updateSettings({ theme: next });
  }

  useEffect(() => {
    const open = () => setPaletteOpen(true);
    const openAskAi = () => setAskAiOpen(true);
    document.addEventListener('ugarits:open-palette', open);
    document.addEventListener('ugarits:open-ask-ai', openAskAi);
    return () => {
      document.removeEventListener('ugarits:open-palette', open);
      document.removeEventListener('ugarits:open-ask-ai', openAskAi);
    };
  }, []);

  useEffect(() => {
    const toggle = () => toggleTheme();
    document.addEventListener('ugarits:toggle-theme', toggle);
    return () => document.removeEventListener('ugarits:toggle-theme', toggle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  // /login renders standalone, no sidebar/topbar/shell — and is never
  // itself wrapped in ProtectedRoute (that would be a redirect loop).
  if (location.pathname === '/login') {
    return (
      <ToastProvider>
        <LoginPage />
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <ProtectedRoute>
        <div className={`app-shell density-${density}`}>
          <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          <div className="main">
            <Topbar
              theme={theme}
              onToggleTheme={toggleTheme}
              onOpenPalette={() => setPaletteOpen(true)}
              onOpenAskAi={() => setAskAiOpen(true)}
              onOpenSidebar={() => setSidebarOpen(true)}
            />
            <div className="content">
              <Breadcrumbs />
              <ErrorBoundary>{element}</ErrorBoundary>
            </div>
            <Footer />
          </div>
        </div>
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        <AskAiDrawer open={askAiOpen} onClose={() => setAskAiOpen(false)} />
      </ProtectedRoute>
    </ToastProvider>
  );
}
